import React, {useState} from 'react';
import {injectIntl} from 'react-intl';
import {withRouter} from 'react-router-dom';
import {createRefetchContainer, graphql} from 'react-relay';
import {BaseLevelWrapper} from './BaseLevelWrapper';
import {GLOBAL_FILTER_FIELD, GLOBAL_FILTER_OPERATOR, GroupProgressCategories} from '../../../../constants';
import {GroupRow} from '../rows/GroupRow';

const PhaseRelayWrapper = ({
	relay,
	tableHeader,
	groupings,
	searchQuery,
	viewer,
	intl,
	rowIndex,
	enabledColumns,
	nextLevelProps,
	sortValue,
	convertToProjectCurrency,
	projectCurrency,
}) => {
	const phases = viewer.company.allPhasesGlobalSearch.edges;
	const company = viewer.company;
	const currency = convertToProjectCurrency && projectCurrency ? projectCurrency : viewer.company.currency;
	const hasNextPage = viewer.company.allPhasesGlobalSearch.pageInfo.hasNextPage;
	const [modalTasksFetching, setModalTasksFetching] = useState(false);

	/**
	 * Grouping filter for task within a phase.
	 *
	 * @param phaseID The phase ID
	 */
	const phaseGroupingFilter = phaseID => {
		return {
			field: GLOBAL_FILTER_FIELD.PROJECT_PHASE,
			operator: GLOBAL_FILTER_OPERATOR.IS,
			value: [phaseID],
		};
	};

	/**
	 * Grouping filter for task without a phase.
	 */
	const noPhaseGroupingFilter = () => {
		return {
			field: GLOBAL_FILTER_FIELD.PHASE_CATEGORY,
			operator: GLOBAL_FILTER_OPERATOR.IS,
			value: GroupProgressCategories.NONE,
		};
	};

	const dataRows = phases.map(phase => {
		return {
			...phase.node,
		};
	});

	/**
	 * Row for 'No-phase' tasks
	 */
	if (company.taskTotals?.numberOfTasks) {
		dataRows.push({
			isNoPhase: true,
			name: intl.formatMessage({id: 'common.no_phase'}),
			taskTotals: company.taskTotals,
		});
	}

	/**
	 * Extract estimation unit from phase
	 *
	 * @param rowData The row data to extract the estimation unit from
	 */
	const estimationUnit = rowData => rowData.estimationUnit;

	const data = {
		rows: dataRows,
		currency,
		groupingFilter: rowData => (rowData.isNoPhase ? noPhaseGroupingFilter() : phaseGroupingFilter(rowData.id)),
		estimationUnit,
	};

	const fetchMore = () => {
		if (!modalTasksFetching) {
			const refetchObject = {};
			setModalTasksFetching(true);
			refetchObject.pageSize = phases.length + 30;
			relay.refetch(refetchObject, null, () => setModalTasksFetching(false));
		}
	};

	const phaseRows = () => {
		return GroupRow(
			data,
			intl,
			rowIndex,
			searchQuery,
			groupings,
			enabledColumns,
			sortValue,
			hasNextPage,
			fetchMore,
			nextLevelProps,
			tableHeader,
			convertToProjectCurrency,
			projectCurrency
		);
	};

	return rowIndex === 1 ? (
		<BaseLevelWrapper
			tableHeader={tableHeader}
			intl={intl}
			enabledColumns={enabledColumns}
			getRows={phaseRows}
			currentLength={phases.length}
			hasNextPage={viewer.company.allPhasesGlobalSearch.pageInfo.hasNextPage}
			refetch={relay.refetch}
		/>
	) : (
		phaseRows()
	);
};

const PhaseRelayWrapperQuery = graphql`
	query PhaseRelayWrapper_Query($searchQuery: TaskSearchQueryType, $pageSize: Int, $convertToProjectCurrency: Boolean) {
		viewer {
			actualPersonId
			component(name: "phase_relay_wrapper")
			...PhaseRelayWrapper_viewer
				@arguments(searchQuery: $searchQuery, pageSize: $pageSize, convertToProjectCurrency: $convertToProjectCurrency)
		}
	}
`;
export {PhaseRelayWrapperQuery};

export default injectIntl(
	withRouter(
		createRefetchContainer(
			PhaseRelayWrapper,
			{
				viewer: graphql`
					fragment PhaseRelayWrapper_viewer on Viewer
					@argumentDefinitions(
						searchQuery: {type: "TaskSearchQueryType"}
						pageSize: {type: "Int"}
						convertToProjectCurrency: {type: "Boolean"}
					) {
						company {
							currency
							taskTotals(
								searchQuery: $searchQuery
								noPhase: true
								convertToProjectCurrency: $convertToProjectCurrency
							) {
								registeredMinutes
								billableActualMinutes
								nonBillableActualMinutes
								estimatePoints
								estimateMinutes
								remainingPoints
								remainingMinutes
								plannedRevenue
								actualRevenue
								remainingRevenue
								forecastRevenue
								recognitionForecastRevenue
								totalActualRevenueRecognition
								totalForecastRevenueToComplete
								surplus
								numberOfTasks
							}
							allPhasesGlobalSearch(first: $pageSize, searchQuery: $searchQuery) {
								pageInfo {
									endCursor
									hasNextPage
								}
								edges {
									node {
										id
										name
										taskTotals(
											searchQuery: $searchQuery
											convertToProjectCurrency: $convertToProjectCurrency
										) {
											registeredMinutes
											billableActualMinutes
											nonBillableActualMinutes
											estimatePoints
											estimateMinutes
											remainingPoints
											remainingMinutes
											plannedRevenue
											actualRevenue
											remainingRevenue
											forecastRevenue
											recognitionForecastRevenue
											totalActualRevenueRecognition
											totalForecastRevenueToComplete
											surplus
											numberOfTasks
										}
									}
								}
							}
						}
					}
				`,
			},
			graphql`
				query PhaseRelayWrapperRefetchQuery(
					$pageSize: Int
					$searchQuery: TaskSearchQueryType
					$convertToProjectCurrency: Boolean
				) {
					viewer {
						...PhaseRelayWrapper_viewer
							@arguments(
								pageSize: $pageSize
								searchQuery: $searchQuery
								convertToProjectCurrency: $convertToProjectCurrency
							)
					}
				}
			`
		)
	)
);
